import { useState } from 'react';
import { Check, X } from 'lucide-react';
import type { TableModel, UniqueConstraintModel } from '@bits2schema/shared';
import { Modal } from '../../components/Modal';
import { useT } from '../../i18n';
import { defaultUniqueConstraintName, ensureUniqueName } from '../mutations';

interface UniqueConstraintDialogProps {
  table: TableModel;
  initial: UniqueConstraintModel | null;
  onCancel: () => void;
  onConfirm: (columns: string[], name: string) => void;
}

/** Formulář pro vytvoření/editaci unique constraintu (E4-US7). */
export function UniqueConstraintDialog({ table, initial, onCancel, onConfirm }: UniqueConstraintDialogProps) {
  const t = useT();
  const [name, setName] = useState(initial?.name ?? '');
  const [columns, setColumns] = useState<string[]>(initial?.columns ?? []);

  const toggle = (columnName: string) =>
    setColumns((previous) =>
      previous.includes(columnName) ? previous.filter((candidate) => candidate !== columnName) : [...previous, columnName],
    );

  const orderedColumns = table.columns
    .map((column) => column.name)
    .filter((columnName) => columns.includes(columnName));
  const valid = orderedColumns.length > 0;

  const confirm = () => {
    if (!valid) return;
    const existingNames = table.uniqueConstraints
      .filter((candidate) => candidate.id !== initial?.id)
      .map((candidate) => candidate.name);
    const finalName = name.trim()
      ? ensureUniqueName(name.trim(), existingNames)
      : defaultUniqueConstraintName(table, orderedColumns);
    onConfirm(orderedColumns, finalName);
  };

  return (
    <Modal
      title={initial ? t('schema.dialog.unique.editTitle') : t('schema.dialog.unique.createTitle')}
      onClose={onCancel}
      actions={
        <>
          <button type="button" onClick={onCancel}>
            <X size={16} />
            {t('common.cancel')}
          </button>
          <button type="button" className="primary" disabled={!valid} onClick={confirm}>
            <Check size={16} />
            {initial ? t('schema.dialog.unique.editTitle') : t('common.create')}
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="field">
          <label htmlFor="unique-name">{t('schema.dialog.unique.nameLabel')}</label>
          <input id="unique-name" value={name} onChange={(event) => setName(event.target.value)} />
        </div>
        <div className="field">
          <span>{t('schema.dialog.unique.columnsLabel')}</span>
          <ul className="inspector__column-list">
            {table.columns.map((column) => (
              <li key={column.id}>
                <label>
                  <input
                    type="checkbox"
                    checked={columns.includes(column.name)}
                    onChange={() => toggle(column.name)}
                  />
                  {column.name}
                </label>
              </li>
            ))}
          </ul>
          {!valid && <p className="hint">{t('schema.dialog.unique.columnsRequired')}</p>}
        </div>
      </div>
    </Modal>
  );
}
